import React, { useCallback, useEffect } from 'react'
import { useFormik } from 'formik'
import { AlertProps, TextField } from '@mui/material'
import * as Yup from 'yup'
import { ApolloError } from '@apollo/client'
import styled from 'styled-components'
import { Body1, H6Bold } from '../../components/Typography'
import {
  AccountType,
  CleanupParam,
  CleanupParamInput,
  RuleType,
  useGetCleanupParamsQuery,
  useUpdateCleanupParamMutation,
} from '../../graphql/generated'
import { Flex, FlexingBoxContent, HeadRow } from '../../components/Flex'
import { spacing } from '../../theme'
import { ButtonGreen } from '../../components/Buttons'
import { captureException } from '../../utils/Sentry'
import { FullWidthBox } from '../../components/Box'
import { TitleWithSub } from '../../components/TitleWithSub'
import { Link } from '../../components/Link'

const StyledTexField = styled(TextField)`
  width: 100%;
`

const JustifiedBody = styled(Body1)`
  text-align: justify;
`

interface Props {
  setSnackbar: React.Dispatch<
    React.SetStateAction<Pick<AlertProps, 'children' | 'severity'> | null>
  >
  onSuccess?: () => void
}

export function AccountingStyleForm({
  setSnackbar,
  onSuccess,
}: Props): React.JSX.Element {
  const { data, error: getCleanupParamsError } = useGetCleanupParamsQuery()
  const [updateCleanupParams] = useUpdateCleanupParamMutation()

  const setError = useCallback(
    (errorMessage: string) => {
      if (errorMessage) {
        setSnackbar({
          children: errorMessage,
          severity: 'error',
        })
      } else {
        setSnackbar(null)
      }
    },
    [setSnackbar],
  )

  const setSuccess = useCallback(() => {
    setSnackbar({
      children:
        'Konfigurationen har uppdaterats. Ändringarna tillämpas nästa gång du läser in bokföringsdata.',
      severity: 'success',
    })
  }, [setSnackbar])

  useEffect(() => {
    if (getCleanupParamsError) {
      setError('något gick fel, vänligen ladda om sidan')
    }
  }, [getCleanupParamsError, setError])

  const accountIdValidationRegex = /^\d{2,4}(,\d{2,4})*$/
  const accountIdValidationErrorMsg = 'felaktigt format'

  const accountFieldValidation = Yup.string()
    .trim()
    .matches(accountIdValidationRegex, accountIdValidationErrorMsg)

  const registerSchema = Yup.object().shape({
    benefits: accountFieldValidation,
    benefitsAndReembursment: accountFieldValidation,
    benefitComp: accountFieldValidation,
    vatComp: accountFieldValidation,
    purchase: accountFieldValidation,
    inventory: accountFieldValidation,
    inventoryChange: accountFieldValidation,
  })

  // Helper function to build cleanup parameters
  const buildCleanupParams = useCallback(
    (
      accountIdsString: string,
      accountType: AccountType,
      ruleType: RuleType,
    ): CleanupParamInput[] => {
      if (!accountIdsString) {
        // set a negative account id value for empty field
        // so that we can know that the user is not doing this kind of operation
        // and ignore the cleanup
        return [
          {
            accountId: -1,
            accountType,
            ruleType,
          },
        ]
      }
      return accountIdsString.split(',').map((id) => ({
        accountId: parseInt(id, 10),
        accountType,
        ruleType,
      }))
    },
    [],
  )

  const {
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
    isSubmitting,
    isValid,
  } = useFormik({
    initialValues: {
      benefits: '',
      benefitsAndReembursment: '',
      benefitComp: '',
      vatComp: '',
      purchase: '',
      inventory: '',
      inventoryChange: '',
    },
    validationSchema: registerSchema,
    onSubmit: async (variables) => {
      setError('')
      const params: CleanupParamInput[] = [
        ...buildCleanupParams(
          variables.benefits,
          AccountType.BenefitAccount,
          RuleType.Benefit,
        ),
        ...buildCleanupParams(
          variables.benefitsAndReembursment,
          AccountType.BenefitAndReimbursementAccount,
          RuleType.Benefit,
        ),
        ...buildCleanupParams(
          variables.benefitComp,
          AccountType.BenefitCompAccount,
          RuleType.Benefit,
        ),
        ...buildCleanupParams(
          variables.vatComp,
          AccountType.VatCompAccount,
          RuleType.ReverseVat,
        ),
        ...buildCleanupParams(
          variables.purchase,
          AccountType.PurchaseAccount,
          RuleType.Inventory,
        ),
        ...buildCleanupParams(
          variables.inventory,
          AccountType.InventoryAccount,
          RuleType.Inventory,
        ),
        ...buildCleanupParams(
          variables.inventoryChange,
          AccountType.InventoryChangeAccount,
          RuleType.Inventory,
        ),
      ]
      try {
        await updateCleanupParams({
          variables: {
            params,
          },
        })

        if (onSuccess) {
          onSuccess()
        } else {
          setSuccess()
        }
      } catch (err) {
        let hasSetError = false
        if (err instanceof ApolloError) {
          if (err.graphQLErrors) {
            err.graphQLErrors.forEach((gqlError) => {
              if (gqlError.extensions?.code === 'BAD_USER_INPUT') {
                // The user has set a account id that is not in the good format (between 2 and 4 digits).
                // All we get is that the accountId as the wrong format. We can't know which of them.
                // So we sett a general error message.
                setError('Alla konto-id måste vara mellan 2 och 4 siffror')
                hasSetError = true
              }
            })
          }
        }
        if (!hasSetError) {
          captureException(err)
          setError('Något gick fel, försök igen senare.')
        }
      }
    },
  })

  // Helper function to populate form fields from fetched data
  const populateFormField = useCallback(
    (
      allParams: Pick<CleanupParam, 'accountId' | 'accountType'>[],
      accountType: AccountType,
      fieldName: keyof typeof values,
      setter: (field: string, value: any, shouldValidate?: boolean) => void,
    ) => {
      const filteredParams = allParams.filter(
        (param) => param.accountType === accountType,
      )
      if (
        filteredParams.length === 0 ||
        filteredParams.filter((param) => param.accountId < 0).length > 0
      ) {
        setter(fieldName, '')
        return
      }
      setter(
        fieldName,
        filteredParams.map((param) => param.accountId).join(','),
      )
    },
    [],
  )

  useEffect(() => {
    if (data?.getCleanupParams) {
      const allParams = data.getCleanupParams
      populateFormField(
        allParams,
        AccountType.BenefitAccount,
        'benefits',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.BenefitAndReimbursementAccount,
        'benefitsAndReembursment',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.BenefitCompAccount,
        'benefitComp',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.VatCompAccount,
        'vatComp',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.PurchaseAccount,
        'purchase',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.InventoryAccount,
        'inventory',
        setFieldValue,
      )
      populateFormField(
        allParams,
        AccountType.InventoryChangeAccount,
        'inventoryChange',
        setFieldValue,
      )
    }
  }, [data, setFieldValue, populateFormField])

  return (
    <FullWidthBox>
      <HeadRow data-testid='accounting-style-form-header'>
        <TitleWithSub title='Redovisningsinformation' sub='' />
      </HeadRow>

      <FlexingBoxContent>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Flex column style={{ gap: spacing.large }}>
            <Flex column>
              <JustifiedBody>
                För att kunna bearbeta dina data mer effektivt behöver vi viss
                information om hur du sköter din redovisning.
              </JustifiedBody>
              <JustifiedBody>
                För varje fall nedan, ange en kommaseparerad lista med
                kontonummer som matchar kriterierna, eller lämna fältet tomt för
                att indikera att inga konton används för det syftet. Du kan ange
                ID:n som är 2 till 4 siffror långa. ID:n med 2 eller 3 siffror
                omfattar alla konton vars nummer börjar med dessa siffror. Till
                exempel omfattar 40 kontona 4000 till 4099.
              </JustifiedBody>
            </Flex>
            <Flex column stretchWidth style={{ gap: spacing.small }}>
              <H6Bold>Förmåner</H6Bold>
              <JustifiedBody>
                <span>
                  När man redovisar förmåner till anställda är det vanligt att
                  först debitera kostnaden på ett kostnadskonto. När lönen sedan
                  bokförs, debiteras även ett förmånskonto. Denna senare post
                  kompenseras genom att kreditera ett motkonto (
                </span>
                <Link
                  href='https://www.bokforingstips.se/artikel/bokforing/bensinforman.aspx'
                  target='_blank'
                >
                  exempel
                </Link>
                <span>).</span>
              </JustifiedBody>
              <Body1>Ange nedan:</Body1>
              <Body1>De konton du använder uteslutande för förmåner.</Body1>
              <StyledTexField
                type='text'
                name='benefits'
                label='förmåner'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.benefits}
                variant='standard'
                error={!!touched.benefits && !!errors.benefits}
                helperText={touched.benefits && errors.benefits}
              />
              <Body1>
                De konton som innehåller både förmåner och kontant utbetalning.
              </Body1>
              <StyledTexField
                type='text'
                name='benefitsAndReembursment'
                label='kostnadsersättningar och förmåner'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.benefitsAndReembursment}
                variant='standard'
                error={
                  !!touched.benefitsAndReembursment &&
                  !!errors.benefitsAndReembursment
                }
                helperText={
                  touched.benefitsAndReembursment &&
                  errors.benefitsAndReembursment
                }
              />
              <Body1>
                De konton som används som motkonto vid bokföring av förmåner.
              </Body1>
              <StyledTexField
                type='text'
                name='benefitComp'
                label='Motkonto'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.benefitComp}
                variant='standard'
                error={!!touched.benefitComp && !!errors.benefitComp}
                helperText={touched.benefitComp && errors.benefitComp}
              />
            </Flex>
            <Flex column stretchWidth style={{ gap: spacing.small }}>
              <H6Bold>Omvänd moms</H6Bold>
              <JustifiedBody>
                <span>
                  Vid köp av varor eller tjänster från utlandet betalas ingen
                  moms till leverantören, och köparen måste istället redovisa
                  momsen enligt reglerna för omvänd skattskyldighet. Detta görs
                  vanligtvis genom att debitera inköpet både på ett inköpskonto
                  (t.ex. 4531) och ett kostnadskonto (t.ex. 6540), varav det ena
                  balanseras genom att kreditera motsvarande belopp på ett
                  justeringskonto för omvänd moms (t.ex. 4599) (
                </span>
                <Link
                  href='https://www.bokforingstips.se/artikel/bokforing/inkop-varor-import.aspx'
                  target='_blank'
                >
                  exempel
                </Link>
                <span>).</span>
              </JustifiedBody>
              <Body1>
                Ange nedan vilka konton du använder som justeringskonto för
                omvänd moms.
              </Body1>
              <StyledTexField
                type='text'
                name='vatComp'
                label='Justering, omvänd moms'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.vatComp}
                variant='standard'
                error={!!touched.vatComp && !!errors.vatComp}
                helperText={touched.vatComp && errors.vatComp}
              />
            </Flex>
            <Flex column stretchWidth style={{ gap: spacing.small }}>
              <H6Bold>Lager</H6Bold>
              <JustifiedBody>
                <span>
                  När man köper varor som ska gå till lagret kan de antingen
                  redovisas direkt på ett lagerkonto (direkt matchning), eller
                  först bokföras på ett inköpskonto och sedan föras över till
                  lagerkontot i samband med lagervärdering (bokslutsmatchning).
                  I båda fallen balanseras förändringen i lagerkontot med en
                  motpost på ett konto för förändring av lager (
                </span>
                <Link
                  href='https://www.bokforingstips.se/artikel/bokforing/varulager.aspx'
                  target='_blank'
                >
                  exempel
                </Link>
                <span>).</span>
              </JustifiedBody>
              <Body1>Ange nedan:</Body1>
              <Body1>De konton du använder för inköp av varor.</Body1>
              <StyledTexField
                type='text'
                name='purchase'
                label='Inköp av varor'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.purchase}
                variant='standard'
                error={!!touched.purchase && !!errors.purchase}
                helperText={touched.purchase && errors.purchase}
              />
              <Body1>
                De konton du avänder för lager, produkter i arbete och pågående
                arbeten
              </Body1>
              <StyledTexField
                type='text'
                name='inventory'
                label='Lager'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.inventory}
                variant='standard'
                error={!!touched.inventory && !!errors.inventory}
                helperText={touched.inventory && errors.inventory}
              />
              <Body1>De konton du använder för förändring av lager.</Body1>
              <StyledTexField
                type='text'
                name='inventoryChange'
                label='Förändring av lager'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.inventoryChange}
                variant='standard'
                error={!!touched.inventoryChange && !!errors.inventoryChange}
                helperText={touched.inventoryChange && errors.inventoryChange}
              />
            </Flex>
            <ButtonGreen
              type='submit'
              disabled={isSubmitting || !isValid}
              data-testid='submit-accounting-style'
            >
              Skicka
            </ButtonGreen>
          </Flex>
        </form>
      </FlexingBoxContent>
    </FullWidthBox>
  )
}
